import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { COMPANY_BILLING_MODEL_LABEL } from "@/lib/status-configs";
import { formatLocalYmd } from "@/lib/company-expiry";
import { ExpiryNote } from "./ExpiryNote";
import type { SupervisorBilling } from "./use-supervisor-billing";

export function BillingTermsCard({ billing }: { billing: SupervisorBilling }) {
  const { t } = useTranslation();
  const isInvoice = billing.billingModel === "invoice";

  return (
    <section className="overflow-hidden rounded-xl border border-border/50 bg-card shadow-sm">
      <div className="border-b border-border/40 px-5 py-3.5">
        <h2 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          {t("supervisor.billing.terms.heading")}
        </h2>
      </div>
      <dl className="divide-y divide-border/30">
        <TermRow label={t("supervisor.billing.terms.model")}>
          {COMPANY_BILLING_MODEL_LABEL[billing.billingModel]}
        </TermRow>
        <TermRow label={t("supervisor.billing.terms.cofunding")}>
          {billing.cofundingPercent >= 100
            ? t("supervisor.billing.terms.cofundingFull")
            : t("supervisor.billing.terms.cofundingPartial", { percent: billing.cofundingPercent })}
        </TermRow>
        {billing.discountPercent > 0 && (
          <TermRow label={t("supervisor.billing.terms.discount")}>
            {t("supervisor.billing.terms.percentValue", { percent: billing.discountPercent })}
          </TermRow>
        )}
        {isInvoice && (
          <TermRow label={t("supervisor.billing.terms.paymentTerm")}>
            {t("supervisor.billing.terms.days", { count: billing.paymentTermDays })}
          </TermRow>
        )}
        {billing.contractExpiresAt && (
          <TermRow label={t("supervisor.billing.terms.contractExpires")}>
            <ExpiryValue ymd={billing.contractExpiresAt} today={billing.today} />
          </TermRow>
        )}
        {!isInvoice && billing.creditExpiresAt && (
          <TermRow label={t("supervisor.billing.terms.creditExpires")}>
            <ExpiryValue ymd={billing.creditExpiresAt} today={billing.today} />
          </TermRow>
        )}
      </dl>
    </section>
  );
}

function TermRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 px-5 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium text-foreground sm:text-right">{children}</dd>
    </div>
  );
}

function ExpiryValue({ ymd, today }: { ymd: string; today: string }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-2 sm:justify-end">
      <span className="tabular-nums">{formatLocalYmd(ymd)}</span>
      <ExpiryNote ymd={ymd} today={today} />
    </span>
  );
}
